import Carregando from '@/shared/components/Carregando';
import DefensorPorComarca from '../reports/DefensorPorComarca.report';
import DefensorPorLotacao from '../reports/DefensorPorLotacao.report';
import DefensorPorNucleo from '../reports/DefensorPorNucleo.report';
import EstagiariosPorDefensoria from '../reports/EstagiarioPorDefensoria.report';
import PrintComponent from '@/shared/components/PrintComponent';
import React, { Fragment, useRef, useState, useEffect, useMemo } from 'react';
import RelatorioDefensoresContent from './tLotacoesPorComarca';
import RelatorioDefensoresLotacoes from './tLotacoesSimplificado';
import RelatorioEstagiariosContent from './tLotacoesPorEstagiarios';
import RelatorioEstagiariosNucleos from './tLotacoesPorEstagiariosNucleos';
import RelatorioEstagiariosSetores from './tLotacoesPorEstagiariosSetores';
import RelatorioNucleosConteudo from './tLotacoesPorNucleo';
import { Button, Modal, Form, Tabs, Tab, Badge } from 'react-bootstrap';
import { EnumRelatorioLotacoes } from '@/modules/business/lotacoes/enums/enum.relatorio.lotacoes';
import { EstagiarioPorSetor } from '@/modules/business/lotacoes/reports/EstagiarioPorSetor.report';
import { FullRelatorio, GetRelatorioNucleo } from '@/modules/reports/relatorio.service';
import { IFullReport, IReportComarca, RelatorioNucleoDto } from '@/api.types';
import { exportFromJsxToPdf } from '@/shared/utils/pdf';
import { generateEstagiariosNucleosPDF } from '../reports/EstagiarioPorNucleo.report'
import { toast } from 'react-toastify';
import { useReactToPrint } from 'react-to-print';

interface FilterModalProps {
  relatorio: IFullReport | null;
  selectedDefensores: string[];
  setSelectedDefensores: (defensores: string[]) => void;
  selectedDefensorias: string[];
  setSelectedDefensorias: (defensorias: string[]) => void;
  selectedComarcas: string[];
  setSelectedComarcas: (comarcas: string[]) => void;
  onClose: () => void;
}

const FilterModal: React.FC<FilterModalProps> = ({
  relatorio,
  selectedDefensores,
  setSelectedDefensores,
  selectedDefensorias,
  setSelectedDefensorias,
  selectedComarcas,
  setSelectedComarcas,
  onClose,
}) => {
  const [searchDefensores, setSearchDefensores] = useState('');
  const [searchDefensorias, setSearchDefensorias] = useState('');
  const [searchComarcas, setSearchComarcas] = useState('');

  const getComarcas = useMemo(() => {
    if (!relatorio) return [];
    return relatorio.comarcas.map((comarca: IReportComarca) =>
      `${comarca.prefixo || ''} ${comarca.nome}`.trim(),
    );
  }, [relatorio]);

  const getDefensorias = useMemo(() => {
    if (!relatorio) return [];
    return relatorio.comarcas
      .filter((comarca) => selectedComarcas.length === 0 || selectedComarcas.includes(`${comarca.prefixo || ''} ${comarca.nome}`.trim()))
      .flatMap((comarca: IReportComarca) => comarca.locais || [])
      .map((defensoria) => defensoria.nome)
      .filter((nome, index, self) => self.indexOf(nome) === index);
  }, [relatorio, selectedComarcas]);

  const getDefensoresComLotacao = useMemo(() => {
    if (!relatorio) return [];
    const hoje = new Date();

    const defensoriasFiltradas = relatorio.comarcas
      .filter(comarca =>
        selectedComarcas.length === 0 ||
        selectedComarcas.includes(`${comarca.prefixo || ''} ${comarca.nome}`.trim())
      )
      .flatMap(comarca => comarca.locais || [])
      .filter(defensoria =>
        selectedDefensorias.length === 0 ||
        selectedDefensorias.includes(defensoria.nome)
      );

    const defensoriaIds = new Set(defensoriasFiltradas.map(d => d.id));

    const lotacoesValidas = relatorio.lotacoes.filter(lotacao =>
      lotacao.defensor_id &&
      defensoriaIds.has(lotacao.defensoria_id) &&
      (!lotacao.data_inicial || new Date(lotacao.data_inicial) <= hoje) &&
      (!lotacao.data_final || new Date(lotacao.data_final) >= hoje)
    );

    const defensores = lotacoesValidas.map(lotacao => {
      const defensor = relatorio.pessoas.find(p => p.id === lotacao.defensor_id);
      return defensor ? [defensor.titulo?.trim(), defensor.nome?.trim()].filter(Boolean).join(' ') : null;
    }).filter((item): item is string => !!item);

    return [...new Set(defensores)];
  }, [relatorio, selectedComarcas, selectedDefensorias]);

  useEffect(() => {
    const validDefensorias = getDefensorias;
    setSelectedDefensorias(selectedDefensorias.filter((d) => validDefensorias.includes(d)));
  }, [getDefensorias, selectedDefensorias]);

  useEffect(() => {
    const validDefensores = getDefensoresComLotacao;
    setSelectedDefensores(selectedDefensores.filter((d) => validDefensores.includes(d)));
  }, [getDefensoresComLotacao, selectedDefensores]);

  const handleSelectAllDefensores = () => {
    const filteredDefensores = getDefensoresComLotacao.filter((defensor) =>
      defensor.toLowerCase().includes(searchDefensores.toLowerCase()),
    );
    setSelectedDefensores([...new Set([...selectedDefensores, ...filteredDefensores])]);
  };

  const handleDeselectAllDefensores = () => {
    setSelectedDefensores([]);
  };

  const handleCheckboxChangeDefensores = (defensor: string) => {
    setSelectedDefensores(
      selectedDefensores.includes(defensor)
        ? selectedDefensores.filter((d) => d !== defensor)
        : [...selectedDefensores, defensor]
    );
  };

  const handleSelectAllDefensorias = () => {
    const filteredDefensorias = getDefensorias.filter((defensoria) =>
      defensoria.toLowerCase().includes(searchDefensorias.toLowerCase()),
    );
    setSelectedDefensorias([...new Set([...selectedDefensorias, ...filteredDefensorias])]);
  };

  const handleDeselectAllDefensorias = () => {
    setSelectedDefensorias([]);
  };

  const handleCheckboxChangeDefensorias = (defensoria: string) => {
    setSelectedDefensorias(
      selectedDefensorias.includes(defensoria)
        ? selectedDefensorias.filter((d) => d !== defensoria)
        : [...selectedDefensorias, defensoria]
    );
  };

  const handleSelectAllComarcas = () => {
    const filteredComarcas = getComarcas.filter((comarca) =>
      comarca.toLowerCase().includes(searchComarcas.toLowerCase()),
    );
    setSelectedComarcas([...new Set([...selectedComarcas, ...filteredComarcas])]);
  };

  const handleDeselectAllComarcas = () => {
    setSelectedComarcas([]);
  };

  const handleCheckboxChangeComarcas = (comarca: string) => {
    setSelectedComarcas(
      selectedComarcas.includes(comarca)
        ? selectedComarcas.filter((c) => c !== comarca)
        : [...selectedComarcas, comarca]
    );
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '10px',
    padding: '10px',
    maxHeight: '50vh',
    overflowY: 'auto',
  };

  const filterTabTitle = (label: string, count: number) => (
    <span className="d-inline-flex align-items-center gap-1">
      {label}
      {count > 0 ? (
        <Badge bg="primary" pill>
          {count}
        </Badge>
      ) : null}
    </span>
  );

  return (
    <Modal
      show={true}
      onHide={onClose}
      size="xl"
      style={{ maxHeight: '100%', overflowY: 'auto' }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Filtros de Resultados</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs defaultActiveKey="defensor" id="filter-tabs" className="mb-3">
          <Tab eventKey="defensor" title={filterTabTitle('Defensores', selectedDefensores.length)}>
            <Form>
              <Form.Group className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="Buscar por nome do defensor..."
                  value={searchDefensores}
                  onChange={(e) => setSearchDefensores(e.target.value)}
                />
              </Form.Group>
              <div className="mb-3">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSelectAllDefensores}
                  className="me-2"
                >
                  Marcar Todos
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleDeselectAllDefensores}
                >
                  Desmarcar Todos
                </Button>
              </div>
              <div style={gridStyle}>
                {relatorio && getDefensoresComLotacao.length > 0 ? (
                  getDefensoresComLotacao
                    .filter((defensor: string) =>
                      defensor.toLowerCase().includes(searchDefensores.toLowerCase()),
                    )
                    .map((defensor: string, index: number) => (
                      <Form.Check
                        key={`defensor-${index}`}
                        id={`defensor-${index}`}
                        type="checkbox"
                        label={defensor}
                        checked={selectedDefensores.includes(defensor)}
                        onChange={() => handleCheckboxChangeDefensores(defensor)}
                        style={{ margin: 0 }}
                      />
                    ))
                ) : (
                  <p>Nenhum defensor disponível para as comarcas e defensorias selecionadas.</p>
                )}
              </div>
            </Form>
          </Tab>
          <Tab eventKey="defensoria" title={filterTabTitle('Defensorias', selectedDefensorias.length)}>
            <Form>
              <Form.Group className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="Buscar por nome da defensoria..."
                  value={searchDefensorias}
                  onChange={(e) => setSearchDefensorias(e.target.value)}
                />
              </Form.Group>
              <div className="mb-3">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSelectAllDefensorias}
                  className="me-2"
                >
                  Marcar Todos
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleDeselectAllDefensorias}
                >
                  Desmarcar Todos
                </Button>
              </div>
              <div style={gridStyle}>
                {relatorio && getDefensorias.length > 0 ? (
                  getDefensorias
                    .filter((defensoria: string) =>
                      defensoria.toLowerCase().includes(searchDefensorias.toLowerCase()),
                    )
                    .map((defensoria: string, index: number) => (
                      <Form.Check
                        key={`defensoria-${index}`}
                        id={`defensoria-${index}`}
                        type="checkbox"
                        label={defensoria}
                        checked={selectedDefensorias.includes(defensoria)}
                        onChange={() => handleCheckboxChangeDefensorias(defensoria)}
                        style={{ margin: 0 }}
                      />
                    ))
                ) : (
                  <p>Nenhuma defensoria disponível para as comarcas selecionadas.</p>
                )}
              </div>
            </Form>
          </Tab>
          <Tab eventKey="comarca" title={filterTabTitle('Comarcas', selectedComarcas.length)}>
            <Form>
              <Form.Group className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="Buscar por nome da comarca..."
                  value={searchComarcas}
                  onChange={(e) => setSearchComarcas(e.target.value)}
                />
              </Form.Group>
              <div className="mb-3">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSelectAllComarcas}
                  className="me-2"
                >
                  Marcar Todos
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleDeselectAllComarcas}
                >
                  Desmarcar Todos
                </Button>
              </div>
              <div style={gridStyle}>
                {relatorio && getComarcas.length > 0 ? (
                  getComarcas
                    .filter((comarca: string) =>
                      comarca.toLowerCase().includes(searchComarcas.toLowerCase()),
                    )
                    .map((comarca: string, index: number) => (
                      <Form.Check
                        key={`comarca-${index}`}
                        id={`comarca-${index}`}
                        type="checkbox"
                        label={comarca}
                        checked={selectedComarcas.includes(comarca)}
                        onChange={() => handleCheckboxChangeComarcas(comarca)}
                        style={{ margin: 0 }}
                      />
                    ))
                ) : (
                  <p>Nenhuma comarca disponível.</p>
                )}
              </div>
            </Form>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
        <Button variant="primary" onClick={onClose}>
          Aplicar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default function RelatorioDefensoresHeader() {
  const contentRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({ contentRef });

  const [agruparComarca, setAgruparComarca] = useState(true);
  const [agruparPorPredio, setAgruparPorPredio] = useState(false);
  const [agruparPorDiretoria, setAgruparPorDiretoria] = useState(false);
  const [agruparAtuacao, setAgruparAtuacao] = useState(true);
  const [exibirEventos, setExibirEventos] = useState(true);
  const [exibirEstagiarios, setExibirEstagiarios] = useState(false);
  const [selecionado, setSelecionado] = useState<EnumRelatorioLotacoes>(EnumRelatorioLotacoes.Defensores_Comarca);
  const [exibirTudo, setExibirTudo] = useState<boolean>(false);
  const [agruparDefensoria, setAgruparDefensoria] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [relatorio, setRelatorio] = useState<IFullReport | null>(null);
  const [relatorioNucleos, setRelatorioNucleos] = useState<RelatorioNucleoDto[]>([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState<{
    defensores: string[];
    defensorias: string[];
    comarcas: string[];
  }>({
    defensores: [],
    defensorias: [],
    comarcas: [],
  });

  useEffect(() => {
    const savedFilters = localStorage.getItem('lotacaoFilters');
    if (savedFilters) {
      setFilters(JSON.parse(savedFilters));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('lotacaoFilters', JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      FullRelatorio(agruparAtuacao, agruparComarca),
      GetRelatorioNucleo(),
    ])
      .then(([rf, rn]) => {
        setRelatorio({
          nome: rf?.nome || '',
          editadoem: rf?.editadoem ? (typeof rf.editadoem === 'string' ? rf.editadoem : new Date(rf.editadoem).toISOString()) : new Date().toISOString(),
          comarcas: rf?.comarcas || [],
          pessoas: rf?.pessoas || [],
          lotacoes: rf?.lotacoes || [],
          atuacoes: rf?.atuacoes || [],
          varas: rf?.varas || [],
          //substituicoes: rf?.substituicoes || [],
        } as IFullReport);
        setRelatorioNucleos(rn)
      })
      .catch((error) => {
        toast.error(String(error));
        setRelatorio(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [agruparComarca, agruparAtuacao]);

  function GetComponente() {
    if (loading) return <Carregando />;
    if (!relatorio) return <div>Erro ao carregar o relatório. Tente novamente.</div>;

    switch (selecionado) {
      case EnumRelatorioLotacoes.Defensores_Comarca:
        return (
          <RelatorioDefensoresContent
            agrupar={agruparComarca}
            datasValidas={agruparAtuacao}
            exibirEventos={exibirEventos}
            exibirEstagiarios={exibirEstagiarios}
            relatorio={relatorio}
            selectedDefensores={filters.defensores}
            selectedDefensorias={filters.defensorias}
            selectedComarcas={filters.comarcas}
            agruparPorPredio={agruparPorPredio}
            agruparPorDiretoria={agruparPorDiretoria}
          />
        );
      case EnumRelatorioLotacoes.Defensores_Lotacao:
        return (
          <RelatorioDefensoresLotacoes
            exibirAnotacoes={exibirEventos}
            exibirTudo={exibirTudo}
            agruparDefensoria={agruparDefensoria}
          />
        );
      case EnumRelatorioLotacoes.Defensores_Nucleo:
        return <RelatorioNucleosConteudo />;
      case EnumRelatorioLotacoes.Estagiarios_Defensoria:
        return <RelatorioEstagiariosContent exibirEventos={exibirEventos} />;
      case EnumRelatorioLotacoes.Estagiarios_Setor:
        return <RelatorioEstagiariosSetores exibirEventos={exibirEventos} />;
      case EnumRelatorioLotacoes.Estagiarios_Nucleo:
        return <RelatorioEstagiariosNucleos />;
      default:
        return null;
    }
  }

  function handleImprimir() {
    if (!relatorio) {
      toast.error('Relatório não carregado. Tente novamente.');
      return;
    }

    switch (selecionado) {
      case EnumRelatorioLotacoes.Defensores_Comarca:
        exportFromJsxToPdf(
          <DefensorPorComarca
            relatorio={relatorio}
            exibirEstagiarios={exibirEstagiarios}
            exibirEventos={exibirEventos}
            agruparAtuacao={agruparAtuacao}
            agruparPorPredio={agruparPorPredio}
            agruparPorDiretoria={agruparPorDiretoria}
            agruparComarca={agruparComarca}
            selectedComarcas={filters.comarcas}
            selectedDefensorias={filters.defensorias}
            selectedDefensores={filters.defensores}
          />
        );
        break;
      case EnumRelatorioLotacoes.Defensores_Lotacao:
        exportFromJsxToPdf(
          <DefensorPorLotacao
            relatorio={relatorio}
            exibirAnotacoes={exibirEventos}
            exibirTudo={exibirTudo}
            agruparDefensoria={agruparDefensoria}
          />
        );
        break;
      case EnumRelatorioLotacoes.Defensores_Nucleo:
        exportFromJsxToPdf(
          <DefensorPorNucleo dados={relatorioNucleos ?? []} />
        );
        break;
      case EnumRelatorioLotacoes.Estagiarios_Defensoria:
        exportFromJsxToPdf(
          <EstagiariosPorDefensoria
            relatorio={relatorio}
            exibirEventos={exibirEventos}
            selectedComarcas={filters.comarcas}
            selectedDefensorias={filters.defensorias}
            selectedDefensores={filters.defensores}
          />
        );
        break;
      case EnumRelatorioLotacoes.Estagiarios_Setor:
        exportFromJsxToPdf(
          <EstagiarioPorSetor
            relatorio={relatorio}
            exibirEventos={exibirEventos}
          />
        );
        break;
      case EnumRelatorioLotacoes.Estagiarios_Nucleo:
        generateEstagiariosNucleosPDF()
          .then((pdfComponent: JSX.Element) => {
            exportFromJsxToPdf(pdfComponent);
          })
          .catch((error) => {
            toast.error('Erro ao gerar PDF: ' + (error));
          });
        break;
      default:
        reactToPrintFn();
        break;
    }
  }

  return (
    <Fragment>
      <div className="row">
        <div className="d-print-none">
          <h3>Tabelas de Lotação</h3>
          <span className="blockquote-footer pb-4 text-end">
            Tabelas com os locais de lotação de defensores, colaboradores e estagiários.
          </span>
        </div>
        <hr />
      </div>

      <div className="row mt-3">
        <div className="col-md-5">
          <select
            className="form-select"
            title="Layout da tabela de lotação."
            onChange={(e) => setSelecionado(Number(e.currentTarget.value))}
          >
            <option value={EnumRelatorioLotacoes.Defensores_Comarca}>
              1 - Defensores, agrupado por comarca e área de atuação.
            </option>
            <option value={EnumRelatorioLotacoes.Defensores_Lotacao}>
              2 - Defensores, por lotação.
            </option>
            <option value={EnumRelatorioLotacoes.Defensores_Nucleo}>
              3 - Defensores, por Núcleo.
            </option>
            <option value={EnumRelatorioLotacoes.Estagiarios_Defensoria}>
              4 - Estagiários, por defensoria.
            </option>
            <option value={EnumRelatorioLotacoes.Estagiarios_Setor}>
              5 - Estagiários, por setor.
            </option>
            <option value={EnumRelatorioLotacoes.Estagiarios_Nucleo}>
              6 - Estagiários, por Núcleo.
            </option>
          </select>
        </div>
        <div className="col-md-5">
          {selecionado === EnumRelatorioLotacoes.Defensores_Comarca && (
            <div className="row">
              <div
                className="col-auto form-check form-switch"
                title="Exibe eventos de férias e substituições."
              >
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="exibireventos"
                  checked={exibirEventos}
                  onChange={(e) => setExibirEventos(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="exibireventos">
                  Exibir Eventos
                </label>
              </div>
              <div
                className="col-auto form-check form-switch"
                title="Exibe coluna de estagiários."
              >
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="exibirestagiarios"
                  checked={exibirEstagiarios}
                  onChange={(e) => setExibirEstagiarios(e.target.checked)}
                />
                <label
                  className="form-check-label"
                  htmlFor="exibirestagiarios"
                >
                  Exibir Estagiários
                </label>
              </div>
              <div
                className="col-auto form-check form-switch"
                title="Agrupa os registros por área de atuação"
              >
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="atuacao"
                  checked={agruparAtuacao}
                  onChange={(e) => setAgruparAtuacao(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="atuacao">
                  Agrupar por atuação
                </label>
              </div>
              <div
                className="col-auto form-check form-switch"
                title="Se deve considerar o campo agrupar no cadastro das comarcas."
              >
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="agrupar"
                  checked={agruparComarca}
                  onChange={(e) => setAgruparComarca(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="agrupar">
                  Agrupar pelo cadastro da comarca
                </label>
              </div>
              <div
                className="col-auto form-check form-switch"
                title="Adicionar agrupamento por prédio."
              >
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="agruparPorPredio"
                  checked={agruparPorPredio}
                  onChange={(e) => setAgruparPorPredio(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="agruparPorPredio">
                  Agrupar por prédio
                </label>
              </div>
              <div
                className="col-auto form-check form-switch"
                title="Agrupa os registros por diretoria regional."
              >
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="agruparPorDiretoria"
                  checked={agruparPorDiretoria}
                  onChange={(e) => setAgruparPorDiretoria(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="agruparPorDiretoria">
                  Agrupar por diretoria
                </label>
              </div>
            </div>
          )}

          {selecionado === EnumRelatorioLotacoes.Defensores_Lotacao && (
            <>
              <div
                className="form-check form-switch"
                title="Exibe eventos de férias e substituições."
              >
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="exibireventos"
                  checked={exibirEventos}
                  onChange={(e) => setExibirEventos(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="exibireventos">
                  Exibir Eventos
                </label>
              </div>
              <div
                className="col-auto form-check form-switch"
                title="Exibir todos os eventos"
              >
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="exibirtudo"
                  checked={exibirTudo}
                  onChange={(e) => setExibirTudo(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="exibirtudo">
                  Exibir Tudo
                </label>
              </div>
              <div
                className="col-auto form-check form-switch"
                title="Agrupa substituições pela defensoria independente do período."
              >
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="agrupardefensoria"
                  checked={agruparDefensoria}
                  onChange={(e) => setAgruparDefensoria(e.target.checked)}
                />
                <label
                  className="form-check-label"
                  htmlFor="agrupardefensoria"
                >
                  Agrupar Defensorias em substituições
                </label>
              </div>
            </>
          )}

          {selecionado === EnumRelatorioLotacoes.Estagiarios_Defensoria && (
            <div className="col-auto row">
              <div
                className="col-auto form-check form-switch"
                title="Exibe eventos de férias e substituições."
              >
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="exibireventos"
                  checked={exibirEventos}
                  onChange={(e) => setExibirEventos(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="exibireventos">
                  Exibir Eventos
                </label>
              </div>
            </div>
          )}
        </div>
        <div className="col-md-1" title="Imprimir Tabela de Lotação">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleImprimir}
          >
            Imprimir
          </button>
        </div>

        {selecionado === EnumRelatorioLotacoes.Defensores_Comarca && (
          <div className="col-md-1" title="Filtros aplicados após pesquisa.">
            <button
              className="btn btn-outline-success"
              onClick={() => setShowModal(true)}
            >
              Filtros
            </button>
          </div>
        )}
      </div>

      <div className="row pt-5">
        <PrintComponent ref={contentRef} value={GetComponente()} />
      </div>

      {showModal && (
        <FilterModal
          relatorio={relatorio}
          selectedDefensores={filters.defensores}
          setSelectedDefensores={(defensores) => setFilters({ ...filters, defensores })}
          selectedDefensorias={filters.defensorias}
          setSelectedDefensorias={(defensorias) => setFilters({ ...filters, defensorias })}
          selectedComarcas={filters.comarcas}
          setSelectedComarcas={(comarcas) => setFilters({ ...filters, comarcas })}
          onClose={() => setShowModal(false)}
        />
      )}
    </Fragment>
  );
}